import { useCallback, useEffect, useRef, useState } from "react";
import type { Id, ScriptDocument } from "@supermanus/document-model";
import {
  canUseFileSystemAccess,
  pickAndWriteDocxFile,
  pickAndWriteRtfFile,
  pickPersistentRtfFile,
  writeToFileHandle,
} from "./exportFile";
import {
  exportEpisodeAudioToRtf,
  exportEpisodeToRtf,
  suggestedRtfFilename,
} from "./exportRtf";
import type { ManuscriptExportOptions } from "./manuscriptExport";
import type { EditorSettings } from "./editorSettings";

export function manuscriptOptionsFromSettings(
  settings: EditorSettings,
): ManuscriptExportOptions {
  return {
    columnOrder: settings.scriptColumnOrder,
    showColumnRule: settings.printColumnRule,
    rowRuleMode: settings.rowRuleMode,
    showHours: settings.durationShowHours,
    showFrames: settings.durationShowFrames,
    fps: settings.durationFps,
    showSetupNumbers: settings.showSetupNumbers,
    showCameraNotes: settings.showCameraNotes,
    showPosition: settings.showPosition,
    defaultSceneHeaderBg: settings.defaultSceneHeaderBg,
    showEpisodeNumbers: settings.showEpisodeNumbers,
  };
}

export function useRtfExport(
  document: ScriptDocument,
  episodeId: Id | null,
  autoExport: boolean,
  settings: EditorSettings,
) {
  const handleRef = useRef<FileSystemFileHandle | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const writeNow = useCallback(
    async (mode: "auto" | "manual", sceneIds?: Id[]) => {
      const options = {
        ...manuscriptOptionsFromSettings(settings),
        ...(sceneIds !== undefined ? { sceneIds } : {}),
      };
      const content = exportEpisodeToRtf(document, episodeId, options);
      const filename = suggestedRtfFilename(document, episodeId, "full");
      try {
        setExportError(null);
        if (handleRef.current) {
          await writeToFileHandle(handleRef.current, content);
        } else if (mode === "manual") {
          // One-shot: prompt + download — no File System Access warning.
          await pickAndWriteRtfFile(filename, content);
        } else {
          return;
        }
        setLastSavedAt(new Date());
      } catch {
        setExportError("Kunne ikke gemme RTF-filen");
      }
    },
    [document, episodeId, settings],
  );

  const exportManual = useCallback(
    async (sceneIds?: Id[]) => {
      await writeNow("manual", sceneIds);
    },
    [writeNow],
  );

  const exportAudioManual = useCallback(
    async (sceneIds?: Id[]) => {
      const options = {
        ...manuscriptOptionsFromSettings(settings),
        ...(sceneIds !== undefined ? { sceneIds } : {}),
      };
      const content = exportEpisodeAudioToRtf(document, episodeId, options);
    const filename = suggestedRtfFilename(document, episodeId, "audio");
    try {
      setExportError(null);
      await pickAndWriteRtfFile(filename, content);
      setLastSavedAt(new Date());
    } catch {
      setExportError("Kunne ikke gemme lydside-RTF");
    }
  }, [document, episodeId, settings]);

  const exportDocxManual = useCallback(async (sceneIds?: Id[]) => {
    const options = {
      ...manuscriptOptionsFromSettings(settings),
      ...(sceneIds !== undefined ? { sceneIds } : {}),
    };
    const { exportEpisodeToDocx, suggestedDocxFilename } = await import(
      "./exportDocx"
    );
    const filename = suggestedDocxFilename(document, episodeId, "full");
    try {
      setExportError(null);
      const blob = await exportEpisodeToDocx(document, episodeId, options);
      await pickAndWriteDocxFile(filename, blob);
      setLastSavedAt(new Date());
    } catch {
      setExportError("Kunne ikke gemme Word-filen");
    }
  }, [document, episodeId, settings]);

  const exportAudioDocxManual = useCallback(async (sceneIds?: Id[]) => {
    const options = {
      ...manuscriptOptionsFromSettings(settings),
      ...(sceneIds !== undefined ? { sceneIds } : {}),
    };
    const { exportEpisodeAudioToDocx, suggestedDocxFilename } = await import(
      "./exportDocx"
    );
    const filename = suggestedDocxFilename(document, episodeId, "audio");
    try {
      setExportError(null);
      const blob = await exportEpisodeAudioToDocx(document, episodeId, options);
      await pickAndWriteDocxFile(filename, blob);
      setLastSavedAt(new Date());
    } catch {
      setExportError("Kunne ikke gemme lydside som Word");
    }
  }, [document, episodeId, settings]);

  const chooseAutoFile = useCallback(async () => {
    const options = manuscriptOptionsFromSettings(settings);
    const content = exportEpisodeToRtf(document, episodeId, options);
    const filename = suggestedRtfFilename(document, episodeId, "full");
    // Persistent handle for auto-export — browser may warn about site access.
    const handle = await pickPersistentRtfFile(filename, content);
    if (handle) {
      handleRef.current = handle;
      setLastSavedAt(new Date());
      setExportError(null);
      return true;
    }
    return false;
  }, [document, episodeId, settings]);

  useEffect(() => {
    if (!autoExport) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void writeNow("auto");
    }, 1500);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [autoExport, document, writeNow]);

  return {
    exportManual,
    exportAudioManual,
    exportDocxManual,
    exportAudioDocxManual,
    chooseAutoFile,
    lastSavedAt,
    exportError,
    hasFileHandle: () => Boolean(handleRef.current),
    canUseFileSystemAccess: canUseFileSystemAccess(),
  };
}
